import { envVars } from '@/config';
import { categoryCompetitions, cups, cupsCompetitionInformation } from '@/mocks/cups';
import type {
  ICategoryCompetitions,
  IGetCompetitionDetailReq,
  IGetCompetitionDetailRes,
  IGetCupInfoReq,
  IGetCupInfoRes,
  IGetCupsCompetitionsReq,
  IGetCupsCompetitionsRes,
  IWalkingFootballCompetition,
} from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { fetchCustomBaseQuery } from '../fetcher';

export const cupApi = createApi({
  reducerPath: 'cupApi',
  baseQuery: fetchCustomBaseQuery({
    baseUrl: envVars.backendUrl,
    mock: false,
  }),
  tagTypes: ['cup'],
  endpoints: (builder) => ({
    getCupInfo: builder.query<
      {
        national_cup_page: IGetCupInfoRes['data']['national_cup_page'];
        winners: IGetCupInfoRes['data']['winners'];
        pagination: IGetCupInfoRes['data']['pagination'];
      },
      IGetCupInfoReq
    >({
      query: ({ page = 1 }) => {
        const url = `/cups-and-fpf/national-cup${page ? `?page=${page}` : ''}`;
        return {
          url,
          isProtected: false,
          forceMock: false,
          mockData: {
            message: 'Cup data fetched Successfully',
            data: {
              national_cup_page: cups[0],
              winners: cups[0].winnings.slice(0, 15),
              pagination: {
                current_page: page,
                from: 1,
                last_page: 6,
                per_page: 15,
                to: 15,
                total: 87,
              },
            },
          },
        };
      },
      transformResponse: (response: IGetCupInfoRes) => ({
        national_cup_page: response.data.national_cup_page,
        winners: response.data.winners,
        pagination: response.data.pagination,
      }),
    }),
    getCupCompetitions: builder.query<
      {
        national_competitions: ICategoryCompetitions[];
        walking_football: IWalkingFootballCompetition | null;
      },
      IGetCupsCompetitionsReq
    >({
      query: ({ competition_type }) => {
        const url = `/cups-and-fpf/national-competitions?filter.competition_type=${competition_type}`;
        return {
          url,
          isProtected: false,
          forceMock: false,
          mockData: {
            message: 'Competitions fetched Successfully',
            data: { national_competitions: categoryCompetitions },
          },
        };
      },
      transformResponse: (response: IGetCupsCompetitionsRes) => ({
        national_competitions: response.data?.national_competitions ?? [],
        walking_football: response.data?.walking_football ?? null,
      })
    }),
    getCompetitionDetail: builder.query<
      IGetCompetitionDetailRes['data']['national_competition'],
      IGetCompetitionDetailReq
    >({
      query: ({ competitionId }) => ({
        url: `/cups-and-fpf/national-competition/${competitionId}`,
        isProtected: false,
        forceMock: false,
        mockData: {
          message: 'Competition fetched Successfully',
          data: cupsCompetitionInformation[0],
        },
      }),
      transformResponse: (response: IGetCompetitionDetailRes) => response.data?.national_competition ?? {},
    }),
  }),
});

export const { useGetCompetitionDetailQuery, useGetCupInfoQuery, useGetCupCompetitionsQuery } = cupApi;
